import { useEffect } from 'react';
import {
  Box,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Image,
  Stack,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { ViewerRestrictedTooltip } from '@/components/overlays/viewer-restricted-tooltip';
import { useAnalytics } from '@/services/analytics';
import welcomeImage from '@/assets/images/people/build-your-messaging-audience.png';

type PeopleWelcomeProps = {
  /** The add-people catalog's URL. */
  addPeopleHref: string;
};

/**
 * The first-run welcome state, shown when the workspace has no profiles at
 * all (the unfiltered probe returned zero).
 */
export function PeopleWelcome({ addPeopleHref }: PeopleWelcomeProps) {
  const { hasPermission } = usePermissions();
  const { track } = useAnalytics();
  const canCreate = hasPermission('journeys/people/create');

  useEffect(() => {
    void track('logs', 'emptyStateViewed', { 'Empty State': 'People' });
  }, [track]);

  return (
    <Stack gap="400" alignInline="center" mt="400">
      <EmptyState variant="promotion">
        <EmptyStateTitle>Build your messaging audience</EmptyStateTitle>
        <EmptyStateDescription>
          Make every message relevant to your audience. Personalize messages, create segments, and build automations
          using real-time data.
        </EmptyStateDescription>
        <EmptyStateActions>
          <ViewerRestrictedTooltip readOnly={!canCreate}>
            {(context) => (
              <Button
                variant="primary"
                isDisabled={!canCreate}
                href={canCreate ? addPeopleHref : undefined}
                icon="chevron-right"
                iconPosition="trailing"
                onClick={() => void track('logs', 'emptyStateActionClicked', { 'Empty State': 'People' })}
                ref={context.reference.setReference}
                {...context.reference.getReferenceProps()}
              >
                Add people
              </Button>
            )}
          </ViewerRestrictedTooltip>
        </EmptyStateActions>
      </EmptyState>

      <Box w="full" style={{ maxWidth: '889px' }}>
        <Image src={welcomeImage} alt="" />
      </Box>
    </Stack>
  );
}
